/**
 * Icon registry — one glyph vocabulary, sized by CSS.
 *
 * WHY THIS FILE EXISTS (Mobile P5): a media query cannot touch an HTML
 * attribute. 113 `<svg>` roots in this app carry `width="16" height="16"`, and
 * every one of them is a glyph that can NEVER grow on a coarse pointer no
 * matter what the coarse register in tokens.css says. That is not a tidiness
 * problem — it is the one blocker the P3 axis cannot route around.
 *
 * It cannot live in `ui/components.js`: the UI consistency sweep skips that
 * file wholesale (it is the kit, and every "don't hand-roll this" rule has to
 * exempt its owner), and a registry whose entire job is to be the single source
 * of glyph markup needs to *be* swept.
 *
 * ── The rules, baked into the emitter rather than documented ──
 *   · no `width`/`height` attribute, ever — size is a CLASS
 *   · one viewBox, `0 0 24 24`
 *   · one stroke-width, `2`
 *   · `currentColor`, so a glyph inherits the ink of whatever it sits in
 *   · `aria-hidden="true"` by default; icons here are decoration beside a
 *     label, and the few that are not pass an explicit `title`
 *   · an unknown name returns `''` and reports — a missing glyph must never
 *     throw inside a template literal and take a whole panel down with it
 *
 * Usage:
 *   icon('trash')                     → HTML string, 16px (--pb-icon default)
 *   icon('trash', { size: 'lg' })     → adds .pb-icon-lg
 *   iconEl('trash')                   → a real <svg> node
 */

import { reportError } from '../core/report-error.js';
import { escapeHTML } from '../shared/html-escape.js';

/** The size classes builder.css defines. `base` is the bare `.pb-icon`. */
const SIZE_CLASS = Object.freeze({
  xs: 'pb-icon-xs',
  sm: 'pb-icon-sm',
  base: '',
  lg: 'pb-icon-lg',
  xl: 'pb-icon-xl'
});

export const ICON_SIZES = Object.freeze(Object.keys(SIZE_CLASS));

/**
 * Wrap glyph interior in the one canonical root.
 *
 * Modelled on `ui/toolbar.js`'s `_mi`, which was already the closest thing this
 * repo had to a convention — the difference is that this one is the only place
 * allowed to write the root, so the convention cannot drift per-file.
 */
const _svg = (inner) =>
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
  `stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${inner}</svg>`;

/**
 * Day one: 24 names, all copy-paste from existing call sites, zero redraw.
 * The 20 from `ui/toolbar.js` (its `MENU_ICONS`) plus the 4 from
 * `ui/account-panel.js`. Every path below is byte-identical to what those two
 * files already shipped — this phase moves glyphs, it does not redesign them.
 */
export const ICONS = Object.freeze({
  // ── Save menu ──
  quick: _svg(
    '<path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z"/><path d="M17 21v-8H7v8"/><path d="M7 3v5h8"/>'
  ),
  full: _svg('<path d="M21 8v13H3V8"/><path d="M1 3h22v5H1z"/><path d="M10 12h4"/>'),
  load: _svg(
    '<path d="M6 14l1.6-3.2A2 2 0 0 1 9.4 10H20a2 2 0 0 1 1.9 2.6l-1.4 5.4A2 2 0 0 1 18.6 20H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4l2 3h6a2 2 0 0 1 2 2v1"/>'
  ),
  cloud: _svg(
    '<path d="M12 13v8"/><path d="m8 17 4-4 4 4"/><path d="M20 16.6A5 5 0 0 0 18 7h-1.3A8 8 0 1 0 4 15.3"/>'
  ),
  history: _svg(
    '<path d="M3 12a9 9 0 1 0 9-9 9.8 9.8 0 0 0-6.7 2.7L3 8"/><path d="M3 3v5h5"/><path d="M12 7v5l4 2"/>'
  ),
  // CMD-2. These were inline SVG literals in ui/toolbar.js with hard-coded
  // width/height attributes — exactly the shape `no-sized-svg-attr` exists to
  // retire, because an attribute cannot respond to a media query. Same paths,
  // now sizable.
  undo: _svg('<polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>'),
  redo: _svg(
    '<polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10"/>'
  ),
  eye: _svg(
    '<path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/>'
  ),
  previews: _svg(
    '<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21"/>'
  ),
  template: _svg(
    '<rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/>'
  ),

  // ── Project menu ──
  templates: _svg(
    '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/>'
  ),
  skin: _svg(
    '<path d="M12 3l1.9 5.2L19 10l-5.1 1.8L12 17l-1.9-5.2L5 10l5.1-1.8L12 3Z"/><path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8L19 15Z"/>'
  ),
  share: _svg(
    '<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="m8.6 13.5 6.8 4"/><path d="m15.4 6.5-6.8 4"/>'
  ),
  startOver: _svg('<path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/>'),

  // ── Export menu ──
  html: _svg('<path d="m18 16 4-4-4-4"/><path d="m6 8-4 4 4 4"/><path d="m14.5 4-5 16"/>'),
  // ── Official project marks ──
  //
  // These two are BRAND LOGOS, not registry line-art, and that is deliberate:
  // the row says "Export 11ty Project" / "Export Astro Project", so the mark
  // that identifies the generator is the generator's own. Nominative use — both
  // projects publish these as press assets.
  //
  // Fetched from astro.build and 11ty.dev; the `d` data below is byte-exact
  // from those files and must stay that way — a redrawn logo is a wrong logo.
  // What this file adds is only the fit into the canonical 24x24 root: a `<g>`
  // whose transform was computed from the real union bbox of the paths
  // (getBBox in Chromium, 1.5 units of pad), plus `fill="currentColor"` so the
  // mark inherits menu ink like every other glyph. `stroke="none"` on Astro
  // because the root's stroke would outline a filled shape; 11ty keeps its own
  // 280-unit stroke, which is part of the wordmark's weight and scales with
  // the transform to about half a pixel.
  //
  // The 11ty source is the GitHub avatar: a #222 plate behind a white wordmark.
  // The plate is DROPPED — a glyph that paints its own background cannot sit in
  // a menu row, and cannot invert for dark mode.
  astro: _svg(
    '<g transform="translate(3.6758 1.5) scale(0.196262)" fill="currentColor" stroke="none"><path d="M27.5893 91.1365C22.7555 86.7178 21.3443 77.4335 23.3583 70.7072C26.8503 74.948 31.6888 76.2914 36.7005 77.0497C44.4374 78.2199 52.0358 77.7822 59.2231 74.2459C60.0453 73.841 60.8052 73.3027 61.7036 72.7574C62.378 74.714 62.5535 76.6892 62.3179 78.6996C61.7452 83.5957 59.3086 87.3778 55.4332 90.2448C53.8835 91.3916 52.2437 92.4167 50.6432 93.4979C45.7262 96.8213 44.3959 100.718 46.2435 106.386C46.2874 106.525 46.3267 106.663 46.426 107C43.9155 105.876 42.0817 104.24 40.6844 102.089C39.2086 99.8193 38.5065 97.3081 38.4696 94.5909C38.4511 93.2686 38.4511 91.9345 38.2733 90.6309C37.8391 87.4527 36.3471 86.0297 33.5364 85.9478C30.6518 85.8636 28.37 87.6469 27.7649 90.4554C27.7187 90.6707 27.6517 90.8837 27.5847 91.1341L27.5893 91.1365Z"/><path d="M0 69.5866C0 69.5866 14.3139 62.6137 28.6678 62.6137L39.4901 29.1204C39.8953 27.5007 41.0783 26.3999 42.4139 26.3999C43.7495 26.3999 44.9325 27.5007 45.3377 29.1204L56.1601 62.6137C73.1601 62.6137 84.8278 69.5866 84.8278 69.5866C84.8278 69.5866 60.5145 3.35233 60.467 3.21944C59.7692 1.2612 58.5911 0 57.0029 0H27.8274C26.2392 0 25.1087 1.2612 24.3634 3.21944C24.3108 3.34983 0 69.5866 0 69.5866Z"/></g>'
  ),
  eleventy: _svg(
    '<g transform="translate(-2.5094 -3.0912) scale(0.001858)" fill="currentColor" stroke="currentColor" stroke-width="280" stroke-miterlimit="10"><path d="m5622 11381c-90 0-135-120-135-361v-5951c0-115-23-169-70-162l-431 108c-79 7-118-72-118-237v-517c0-143 43-224 129-242l1422-366c11-3 27-5 48-5 79 0 118 84 118 253v7120c0 241-47 361-140 361zm3683 11c-144 0-268-10-374-30s-216-65-331-135-209-166-283-288-134-293-180-512-70-479-70-781v-2762c0-72-20-108-59-108h-334c-90 0-135-86-135-258v-291c0-176 45-264 135-264h334c39 0 59-48 59-145l97-2095c11-190 57-285 140-285h539c90 0 135 95 135 285v2095c0 97 21 145 65 145h687c90 0 135 88 135 264v291c0 172-45 258-135 258h-689c-25 0-42 6-51 19-9 12-13 42-13 89v2779c0 208 13 382 40 520s66 240 118 304 104 108 156 129c52 22 116 32 191 32h382c97 0 145 67 145 199v323c0 147-52 221-156 221zm2067 646c82 0 154-67 215-202s92-326 92-574c0-58-36-257-108-598l-1056-4389c-7-50-11-90-11-119 0-129 27-194 81-194h652c50 0 91 17 124 51s58 103 75 207l700 3705c14 43 23 65 27 65 14 0 22-20 22-59l549-3695c14-108 37-180 67-218s69-57 116-57h452c61 0 92 70 92 210 0 32-4 74-11 124l-959 4993c-75 413-158 729-248 948s-190 368-302 447a776 776 0 0 1 -442 124h-54c-291 0-488-77-592-232-29-32-43-115-43-248 0-266 43-399 129-399 7 0 72 18 194 54 124 38 203 56 239 56zm-8460-647c-90 0-135-120-135-361v-5951c0-115-23-169-70-162l-431 108c-79 7-118-72-118-237v-517c0-143 43-224 129-242l1423-367c11-3 27-5 48-5 79 0 118 84 118 253v7120c0 241-47 361-140 361z"/></g>'
  ),

  // ── Row 1 identity & destination (CMD-3) ──
  // Both were inline, attribute-sized SVG literals in `ui/toolbar.js`. Audited
  // against the registry before adding, as the plan requires: neither has an
  // existing semantic here. `sidebar` is the panel-left glyph (no equivalent);
  // `globe` means "the public web", which is what Publish targets — `cloud` is
  // already taken by "save to the cloud" and `share` by the collaborator graph,
  // and reusing either would make two different destinations look the same.
  //
  // Deliberately NOT added: a house for the project chip (the existing
  // `projects` folder already means "a project") and a second sparkle for the
  // agent button (`skin` is the identical two-sparkle path — the agent glyph
  // was that drawing with a gradient fill, and the gradient is now applied in
  // CSS instead of being a second copy of the outline).
  sidebar: _svg(
    '<rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/>'
  ),
  globe: _svg(
    '<circle cx="12" cy="12" r="9"/><path d="M3 12h18"/><path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18Z"/>'
  ),

  // ── Text formatting (CMD-6) ──
  // Row 2's inline marks. Drawn as letterforms rather than as the usual
  // heavy-B / slanted-I, so they read at 16px and inherit the stroke weight
  // every other glyph in the bar uses.
  bold: _svg('<path d="M7 5h6a3.5 3.5 0 0 1 0 7H7z"/><path d="M7 12h7a3.5 3.5 0 0 1 0 7H7z"/>'),
  italic: _svg(
    '<line x1="19" y1="5" x2="11" y2="5"/><line x1="13" y1="19" x2="5" y2="19"/><line x1="15" y1="5" x2="9" y2="19"/>'
  ),

  // ── Viewport switcher (CMD-5) ──
  // The three device glyphs, moved off `ui/viewport-controls.js`'s VIEWPORTS
  // table where they were attribute-sized inline literals inside a data
  // structure — which is why they evaded notice for so long: the sweep sees
  // markup, and nobody reads a config object looking for a sized glyph.
  // Same paths, now sizable.
  desktop: _svg(
    '<rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>'
  ),
  tablet: _svg(
    '<rect x="4" y="2" width="16" height="20" rx="2"/><line x1="12" y1="18" x2="12" y2="18"/>'
  ),
  mobile: _svg(
    '<rect x="7" y="2" width="10" height="20" rx="2"/><line x1="12" y1="18" x2="12" y2="18"/>'
  ),
  // The zoom popover's two steppers. `plus` already exists and is reused for
  // "zoom in"; `minus` is genuinely new — nothing in the registry meant it.
  minus: _svg('<line x1="5" y1="12" x2="19" y2="12"/>'),

  // ── Help and preview (CMD-8) ──
  // Three genuinely new semantics, checked against the whole registry first:
  // nothing here asked a question, started something, or stopped it. `eye` is
  // "visible", which is a section's visibility, not the app's preview mode —
  // giving preview the eye would have made one glyph mean two states.
  help: _svg(
    '<circle cx="12" cy="12" r="9"/><path d="M9.2 9.2a2.9 2.9 0 0 1 5.6 1c0 1.9-2.8 2.4-2.8 4"/><line x1="12" y1="17.5" x2="12.01" y2="17.5"/>'
  ),
  play: _svg('<polygon points="6 4 20 12 6 20 6 4"/>'),
  stop: _svg('<rect x="6" y="6" width="12" height="12" rx="2"/>'),
  // Outage alerts. A bell rather than a warning triangle: the entry point opens
  // a NOTIFICATION setting, and a triangle on a menu row that is usually resting
  // reads as "something is wrong right now".
  bell: _svg(
    '<path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.7 21a2 2 0 0 1-3.4 0"/>'
  ),

  // ── Overflow menu ──
  tour: _svg(
    '<circle cx="12" cy="12" r="10"/><polygon points="16.2 7.8 14.1 14.1 7.8 16.2 9.9 9.9 16.2 7.8"/>'
  ),
  shortcuts: _svg(
    '<rect x="2" y="4" width="20" height="16" rx="2"/><path d="M6 8h.01M10 8h.01M14 8h.01M18 8h.01M8 12h.01M12 12h.01M16 12h.01M7 16h10"/>'
  ),
  code: _svg('<path d="m18 16 4-4-4-4"/><path d="m6 8-4 4 4 4"/><path d="m14.5 4-5 16"/>'),
  links: _svg(
    '<path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7"/><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7"/>'
  ),
  revision: _svg(
    '<line x1="6" y1="3" x2="6" y2="15"/><circle cx="18" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M18 9a9 9 0 0 1-9 9"/>'
  ),

  // ── Page-tab context menu ──
  rename: _svg('<path d="M17 3a2.8 2.8 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/>'),
  duplicate: _svg(
    '<rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>'
  ),
  // MCP-5: "Report a problem" on a catalog section (section-code-modal).
  flag: _svg('<path d="M4 22V4"/><path d="M4 4h12l-2 4 2 4H4"/>'),
  trash: _svg(
    '<path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>'
  ),

  // ── Shell chrome (Mobile P6) ──
  // `close` replaces the literal '×' character the modal header used, which had
  // no way to track the glyph ramp. The other four come off the glass island,
  // which drew them on a 16×16 grid at three different stroke widths — a
  // mechanical rescale to the 24 grid, not a redesign.
  close: _svg('<path d="M18 6 6 18"/><path d="m6 6 12 12"/>'),
  sliders: _svg(
    '<line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/><circle cx="7.5" cy="6" r="2.25" fill="currentColor" stroke="none"/><circle cx="15" cy="12" r="2.25" fill="currentColor" stroke="none"/><circle cx="10.5" cy="18" r="2.25" fill="currentColor" stroke="none"/>'
  ),
  chevronUp: _svg('<polyline points="6 15 12 9 18 15"/>'),
  chevronDown: _svg('<polyline points="6 9 12 15 18 9"/>'),
  // BC-9: page reorder in a horizontal strip, and the page-actions trigger.
  chevronLeft: _svg('<polyline points="15 18 9 12 15 6"/>'),
  chevronRight: _svg('<polyline points="9 18 15 12 9 6"/>'),
  more: _svg(
    '<circle cx="5" cy="12" r="1.5" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"/><circle cx="19" cy="12" r="1.5" fill="currentColor" stroke="none"/>'
  ),
  scrollState: _svg('<path d="M4 5h16M4 19h16"/><path d="M12 3v12"/><path d="m8 11 4 4 4-4"/>'),
  plus: _svg('<line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>'),
  reset: _svg('<path d="M10.5 16.5 4.5 10.5l6-6"/><path d="M19.5 19.5a9 9 0 0 0-9-9h-6"/>'),

  // ── Account panel ──
  projects: _svg(
    '<path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>'
  ),
  profile: _svg('<circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 3.6-6.5 8-6.5s8 2.5 8 6.5"/>'),
  settings: _svg(
    '<path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/>'
  ),
  logout: _svg(
    '<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="M16 17l5-5-5-5"/><path d="M21 12H9"/>'
  ),

  // ── Admin console ──
  overview: _svg('<path d="M3 12h4l3 8 4-16 3 8h4"/>'),
  access: _svg(
    '<path d="M15 7h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h3"/><path d="M9 7V5a3 3 0 0 1 6 0v2"/>'
  ),
  billing: _svg(
    '<path d="M3 10h18"/><rect x="3" y="7" width="18" height="10" rx="1"/><path d="M7 14h3"/>'
  ),
  arrowLeft: _svg('<path d="m15 18-6-6 6-6"/>'),
  lock: _svg(
    '<path d="M5 11V7a7 7 0 0 1 14 0v4"/><rect x="5" y="11" width="14" height="10" rx="1"/><path d="M12 15v2"/>'
  ),

  // ── Project switcher ──
  // Both moved off `ui/project-history-modal.js`, which drew them at 15×15 via
  // width/height attributes — the exact shape `no-sized-svg-attr` exists to
  // catch. `check` is new: the selection control paints its own tick rather
  // than relying on a native checkbox glyph nothing can style.
  search: _svg('<circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>'),
  // Find & Replace's "show the replace row" (SYS-5): an arrow into a line.
  replace: _svg('<polyline points="5 8 9 12 5 16"/><line x1="9" y1="12" x2="20" y2="12"/>'),
  check: _svg('<polyline points="20 6 9 17 4 12"/>'),

  // ── Section card actions ──
  // Two glyphs, ONE outline: `starFilled` is `star` plus `fill="currentColor"`
  // on the same path. A toggle whose two states are different drawings reads as
  // two different controls when it flips; this one only changes ink.
  star: _svg(
    '<path d="m12 3.5 2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 9.7l5.9-.9z"/>'
  ),
  starFilled: _svg(
    '<path fill="currentColor" d="m12 3.5 2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 9.7l5.9-.9z"/>'
  ),

  // ── Align bar (UX Pack F4c) ──
  // Box alignment glyphs: a guide line plus two bars snapped to it. Distinct
  // from text-align glyphs on purpose — these move the block's BOX.
  alignLeft: _svg(
    '<path d="M4 3v18"/><rect x="7" y="6" width="12" height="4"/><rect x="7" y="14" width="7" height="4"/>'
  ),
  alignCenterH: _svg(
    '<path d="M12 3v18"/><rect x="6" y="6" width="12" height="4"/><rect x="8.5" y="14" width="7" height="4"/>'
  ),
  alignRight: _svg(
    '<path d="M20 3v18"/><rect x="5" y="6" width="12" height="4"/><rect x="10" y="14" width="7" height="4"/>'
  ),
  distributeV: _svg(
    '<path d="M4 4h16"/><path d="M4 20h16"/><rect x="8" y="8" width="8" height="3"/><rect x="8" y="13" width="8" height="3"/>'
  )
});

export const ICON_NAMES = Object.freeze(Object.keys(ICONS));

/**
 * Render a glyph as an HTML string.
 *
 * @param {string} name — a key of ICONS
 * @param {object} [opts]
 * @param {'xs'|'sm'|'base'|'lg'|'xl'} [opts.size='base'] — maps to a CLASS, never an attribute
 * @param {string} [opts.className] — extra classes, appended
 * @param {string} [opts.title] — when present the glyph becomes an image with an
 *   accessible name instead of decoration; use only when the glyph is the ONLY
 *   thing conveying the meaning
 * @returns {string} HTML, or '' for an unknown name
 */
export function icon(name, opts = {}) {
  const markup = ICONS[name];
  if (!markup) {
    // Silent to the user by design: a template literal that interpolates a
    // missing glyph must degrade to a label with no icon, not to a toast
    // storm — some of these render once per list row.
    reportError('icons', new Error(`unknown icon "${name}"`), { user: 'silent' });
    return '';
  }

  const { size = 'base', className = '', title = '' } = opts;
  const sizeClass = Object.hasOwn(SIZE_CLASS, size) ? SIZE_CLASS[size] : SIZE_CLASS.base;
  const classes = ['pb-icon', sizeClass, className].filter(Boolean).join(' ');

  let out = markup.replace('<svg ', `<svg class="${classes}" `);
  if (title) {
    out = out
      .replace(' aria-hidden="true"', ` role="img" aria-label="${escapeHTML(title)}"`)
      .replace('>', `><title>${escapeHTML(title)}</title>`);
  }
  return out;
}

/**
 * The same glyph as a live node.
 *
 * Parsed as `text/html`, NOT `image/svg+xml`. The registry's markup carries no
 * `xmlns`, so the XML parser gives the root a null namespace and `importNode`
 * then yields an element that is an `svg` by name only and renders as nothing.
 * The HTML parser puts `<svg>` into the SVG namespace by itself — which is also
 * exactly what happens at every `innerHTML` call site, so this path and the
 * string path produce the same element.
 *
 * @returns {SVGElement|null} null for an unknown name
 */
export function iconEl(name, opts = {}) {
  const markup = icon(name, opts);
  if (!markup) return null;
  // `ui/components.js` calls this while building every modal header, so a throw
  // here would take down every dialog in the app. Callers treat null as "no
  // glyph" and still render an operable control.
  if (typeof DOMParser !== 'function' || typeof document === 'undefined') return null;
  const el = new DOMParser().parseFromString(markup, 'text/html').body.querySelector('svg');
  if (!el) {
    reportError('icons', new Error(`icon "${name}" failed to parse`), { user: 'silent' });
    return null;
  }
  return document.importNode(el, true);
}
